import { isEmpty, groupBy } from "lodash"
import { downloadRegistry, type DownloadData, type DownloadDataRegistration, type DownloadSheet } from "../context/download-registry"

/**
 * Creates a function that aggregates download data from the registry in panel order.
 * Invoked when the user clicks Download; reads the registry at call time (no subscription).
 *
 * Main logic:
 * 1. Get panel IDs in layout order (left-to-right, top-to-bottom).
 * 2. For each panel, split its registrations into:
 *    - Combinable: sources that share a sheet (e.g. KPI Risk + Composition → one "KPI" sheet).
 *    - Standalone: each source becomes its own sheet.
 * 3. Combinable registrations are grouped by sheetName and merged in registration order,
 *    with a blank row separating each chunk. Registration order matches component render order.
 * 4. Standalone registrations each produce one sheet.
 * 5. Sheets are emitted in panel order, then combined and standalone interleaved per panel.
 * 6. Duplicate sheet names are suffixed with a counter (e.g. "AI Table", "AI Table 2").
 */
export function createReduceDownloadData(getPanelOrder: () => string[]) {
  return (): { sheets: DownloadSheet[] } | null => {
    const allRegistrations = downloadRegistry.getRegistrations()
    const orderedPanelIds = getPanelOrder() ?? []

    const sheets = orderedPanelIds.flatMap((panelId) => {
      const panelRegistrations = allRegistrations.get(panelId)
      return panelRegistrations ? buildSheetsForPanel(panelRegistrations) : []
    })

    return isEmpty(sheets) ? null : { sheets: deduplicateSheetNames(sheets) }
  }
}

type RegistrationEntry = [string, DownloadDataRegistration]

function hasContent(chunk: DownloadData | null): chunk is DownloadData {
  return chunk != null && !(isEmpty(chunk.headers) && isEmpty(chunk.rows))
}

/** Splits a panel's registrations into combinable and standalone, then builds both sheet types. */
function buildSheetsForPanel(panelRegistrations: Map<string, DownloadDataRegistration>): DownloadSheet[] {
  const entries = [...panelRegistrations]
  const combinableEntries = entries.filter(([, registration]) => registration.combineWithOthers)
  const standaloneEntries = entries.filter(([, registration]) => !registration.combineWithOthers)

  return [...buildCombinedSheets(combinableEntries), ...buildStandaloneSheets(standaloneEntries)]
}

/** Groups combinable entries by sheet name, merging each group into a single sheet. */
function buildCombinedSheets(combinableEntries: RegistrationEntry[]): DownloadSheet[] {
  const groups = Object.values(groupBy(combinableEntries, ([, registration]) => registration.sheetName))

  return groups.flatMap(buildSheetFromGroup)
}

/** Merges all entries in a same-sheet group into one sheet, with a blank row between each chunk. */
function buildSheetFromGroup(entriesInGroup: RegistrationEntry[]): DownloadSheet[] {
  const chunks = entriesInGroup
    .map(([, registration]) => registration.getData())
    .filter(hasContent)

  if (chunks.length === 0) return []

  const merged = mergeDataChunks(chunks)
  const [, { sheetName: sharedSheetName }] = entriesInGroup[0]

  return merged.headers.length > 0 || merged.rows.length > 0
    ? [{ name: sharedSheetName, rows: [merged.headers, ...merged.rows] }]
    : []
}

/** Converts each standalone registration into its own sheet; skips empty results. */
function buildStandaloneSheets(standaloneEntries: RegistrationEntry[]): DownloadSheet[] {
  return standaloneEntries.flatMap(([, registration]) => {
    const data = registration.getData()
    if (!hasContent(data)) return []

    return [{ name: registration.sheetName, rows: [data.headers, ...data.rows] }]
  })
}

/**
 * Ensures all sheet names are unique. Duplicate names get a numeric suffix
 * starting at 2 (e.g. "AI Table", "AI Table 2", "AI Table 3").
 *
 * The counter keeps incrementing until the candidate name is not already
 * present in the emitted set, preventing collisions with pre-existing names
 * (e.g. input ["X", "X 2", "X"] → ["X", "X 2", "X 3"], not ["X", "X 2", "X 2"]).
 */
function deduplicateSheetNames(sheets: DownloadSheet[]): DownloadSheet[] {
  const emittedNames = new Set<string>()
  const nextCounterByBase = new Map<string, number>()

  return sheets.map((sheet) => {
    if (!emittedNames.has(sheet.name)) {
      emittedNames.add(sheet.name)
      return sheet
    }

    let counter = nextCounterByBase.get(sheet.name) ?? 2
    let candidate = `${sheet.name} ${counter}`
    while (emittedNames.has(candidate)) {
      counter += 1
      candidate = `${sheet.name} ${counter}`
    }
    nextCounterByBase.set(sheet.name, counter + 1)
    emittedNames.add(candidate)
    return { ...sheet, name: candidate }
  })
}

/**
 * Merges multiple non-empty data chunks into one, inserting a blank row between each chunk.
 * Uses headers from the first chunk. Callers must ensure chunks is non-empty.
 */
function mergeDataChunks(chunks: DownloadData[]): DownloadData {
  const headers = chunks[0].headers
  const rows = chunks.flatMap((chunk, index) => (index === 0 ? chunk.rows : [[], ...chunk.rows]))

  return { headers, rows }
}
